import React, { ButtonHTMLAttributes, forwardRef } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'outline' | 'ghost';
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      icon,
      iconPosition = 'left',
      className = '',
      children,
      disabled,
      style,
      ...props
    },
    ref
  ) => {
    const baseClasses = 'btn';

    const variantClasses: Record<ButtonVariant, string> = {
      primary: 'btn-primary',
      secondary: 'btn-secondary',
      success: 'btn-success',
      danger: 'btn-danger',
      warning: 'btn-warning',
      info: 'btn-outline',
      outline: 'btn-outline',
      ghost: 'btn-ghost',
    };

    const sizeClasses: Record<ButtonSize, string> = {
      xs: 'btn-xs',
      sm: 'btn-sm',
      md: '',
      lg: 'btn-lg',
      xl: 'btn-xl',
    };

    // Force button sizes with inline styles as absolute fallback
    const sizeStyles: Record<ButtonSize, React.CSSProperties> = {
      xs: {
        paddingLeft: '0.5rem',
        paddingRight: '0.5rem',
        paddingTop: '0.25rem',
        paddingBottom: '0.25rem',
        fontSize: '0.75rem',
        lineHeight: '1rem',
      },
      sm: {
        paddingLeft: '0.75rem',
        paddingRight: '0.75rem',
        paddingTop: '0.375rem',
        paddingBottom: '0.375rem',
        fontSize: '0.875rem',
      },
      md: {},
      lg: {
        paddingLeft: '1.5rem',
        paddingRight: '1.5rem',
        paddingTop: '0.75rem',
        paddingBottom: '0.75rem',
        fontSize: '1.125rem',
      },
      xl: {
        paddingLeft: '2rem',
        paddingRight: '2rem',
        paddingTop: '1rem',
        paddingBottom: '1rem',
        fontSize: '1.25rem',
      },
    };

    const classes = [
      baseClasses,
      variantClasses[variant],
      sizeClasses[size],
      fullWidth ? 'w-full' : '',
      loading ? 'opacity-70 cursor-wait' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Force Info button styling with inline styles as fallback
    const infoButtonStyle = variant === 'info' ? {
      backgroundColor: '#3B82F6',
      color: 'white',
    } : {};

    // Combine all inline styles
    const combinedStyle = { 
      ...sizeStyles[size],
      ...infoButtonStyle, 
      ...style 
    };

    return (
      <button
        ref={ref}
        className={classes}
        disabled={disabled || loading}
        style={combinedStyle}
        {...props}
      >
        {!loading && icon && iconPosition === 'left' && (
          <span className="mr-2">{icon}</span>
        )}
        {children}
        {loading && (
          <>
            &nbsp;
            <span className="spinner-sm ml-1 inline-block" role="status" aria-label="Loading"></span>
          </>
        )}
        {!loading && icon && iconPosition === 'right' && (
          <span className="ml-2">{icon}</span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;